import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Textarea from "@cloudscape-design/components/textarea"
import React, { useState, useEffect } from "react"
import type { DTADocumentContent } from "@/generated/sdk"
import type { CreateProposalRequest } from "@/services/dtaCollaborationApi"
import { useCreateProposal } from "@/hooks/queries/useCollaboration"

// A section's current value is one document_content field's value.
type DocumentContentValue = DTADocumentContent[keyof DTADocumentContent]

const stringifyContentValue = (value: DocumentContentValue | null | undefined): string =>
  value == null ? "" : typeof value === "string" ? value : JSON.stringify(value, null, 2)

interface ProposalFormProps {
  dtaId: string
  vendorCompany: string
  sections: Array<{ id: string; title: string; currentValue?: DocumentContentValue }>
  version: string
  onProposalCreated?: () => void
}

const ProposalForm: React.FC<ProposalFormProps> = ({
  dtaId,
  vendorCompany,
  sections,
  version,
  onProposalCreated,
}) => {
  const createProposal = useCreateProposal(dtaId)
  const [sectionReference, setSectionReference] = useState("")
  const [originalValue, setOriginalValue] = useState<DocumentContentValue | null>(null)
  const [proposedValue, setProposedValue] = useState("")
  const [rationale, setRationale] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (sectionReference) {
      const section = sections.find((s) => s.id === sectionReference)
      if (section?.currentValue) {
        setOriginalValue(section.currentValue)
        setProposedValue(stringifyContentValue(section.currentValue))
      } else {
        setOriginalValue(null)
        setProposedValue("")
      }
    }
  }, [sectionReference, sections])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!sectionReference || !proposedValue.trim() || !rationale.trim()) {
      setError("Please select a section, enter a proposed value, and provide a rationale")
      return
    }

    setError(null)
    setSuccess(false)

    try {
      const request: CreateProposalRequest = {
        section_reference: sectionReference,
        original_value: stringifyContentValue(originalValue),
        proposed_value: proposedValue.trim(),
        rationale: rationale.trim(),
        vendor_company: vendorCompany,
        version,
      }

      await createProposal.mutateAsync(request)

      setSuccess(true)
      setProposedValue("")
      setRationale("")
      setSectionReference("")
      setOriginalValue(null)

      if (onProposalCreated) {
        setTimeout(() => {
          onProposalCreated()
          setSuccess(false)
        }, 2000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create proposal")
    }
  }

  const sectionOptions: SelectProps.Options = sections.map((section) => ({
    label: section.title || section.id.replace(/_/g, " ").replace(/\./g, " → "),
    value: section.id,
  }))

  const selectedOption: SelectProps.Option | null = sectionReference
    ? (sectionOptions.find(
        (opt) => (opt as SelectProps.Option).value === sectionReference,
      ) as SelectProps.Option) || null
    : null

  const selectedSection = sections.find((s) => s.id === sectionReference)

  return (
    <Container variant="stacked" header={<Header variant="h2">Propose a Change</Header>}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <SpaceBetween size="l">
          <FormField label="Section" controlId="proposal-section">
            <Select
              selectedOption={selectedOption}
              onChange={({ detail }) => setSectionReference(detail.selectedOption.value ?? "")}
              options={sectionOptions}
              placeholder="Select a section..."
            />
          </FormField>

          {originalValue !== null && (
            <FormField label="Current Value">
              <Box variant="code">
                <pre>{stringifyContentValue(originalValue)}</pre>
              </Box>
            </FormField>
          )}

          <FormField
            label="Proposed Value"
            controlId="proposed-value"
            constraintText={
              typeof originalValue === "object" ? "JSON format recommended" : "Enter the new value"
            }
          >
            <Textarea
              value={proposedValue}
              onChange={({ detail }) => setProposedValue(detail.value)}
              rows={6}
              placeholder={
                selectedSection?.currentValue
                  ? "Enter your proposed value..."
                  : "Enter the proposed value for this section..."
              }
            />
          </FormField>

          <FormField label="Rationale / Justification" controlId="rationale">
            <Textarea
              value={rationale}
              onChange={({ detail }) => setRationale(detail.value)}
              rows={4}
              placeholder="Explain why this change is needed..."
            />
          </FormField>

          {error && <Alert type="error">{error}</Alert>}

          {success && (
            <Alert type="success">
              <Icon name="status-positive" size="normal" /> Proposal submitted successfully!
            </Alert>
          )}

          <Box float="right">
            <Button
              variant="primary"
              formAction="submit"
              loading={createProposal.isPending}
              disabled={
                createProposal.isPending ||
                !sectionReference ||
                !proposedValue.trim() ||
                !rationale.trim()
              }
            >
              {createProposal.isPending ? "Submitting..." : "Submit Proposal"}
            </Button>
          </Box>
        </SpaceBetween>
      </form>
    </Container>
  )
}

export default ProposalForm
